import type { ProjectDetails, ProjectStatus, ProjectPriority, BillingType } from '@/types/project';
import type { ProjectListItem } from '@/services/projectsApi';

/**
 * Maps a ProjectListItem (from paginated API) to a ProjectDetails-compatible format
 * for use with components that expect the full ProjectDetails type.
 * Missing properties are filled with sensible defaults.
 */
export function mapListItemToDetails(item: ProjectListItem): ProjectDetails {
  const startDate = item.startDate ? new Date(item.startDate) : new Date();
  const endDate = item.endDate ? new Date(item.endDate) : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000); // 90 days from now

  // Map billing type string to BillingType, with fallback
  const billingTypeMap: Record<string, BillingType> = {
    'time-and-materials': 'time-and-materials',
    'fixed-price': 'fixed-price',
    'retainer': 'retainer',
    'non-billable': 'non-billable',
  };
  const billingType: BillingType = billingTypeMap[item.billingType] || 'time-and-materials';

  // Map priority string to ProjectPriority, with fallback for invalid/missing values
  const priorityMap: Record<string, ProjectPriority> = {
    'high': 'high',
    'medium': 'medium',
    'low': 'low',
  };
  const normalizedPriority = item.priority?.toLowerCase() || '';
  const priority: ProjectPriority = priorityMap[normalizedPriority] || 'medium';

  return {
    id: item.id,
    code: item.code,
    name: item.name,
    description: '', // Not available in list item
    clientId: item.clientId,
    clientName: item.clientName,
    clientSector: 'enterprise', // Default - not available in list item
    status: item.status as ProjectStatus,
    priority,
    billable: item.billable,
    billingType,
    timeline: {
      startDate,
      endDate,
      milestones: [],
    },
    projectManager: item.projectManager ?? '',
    teamMembers: [],
    teamSize: item.teamSize ?? 0,
    timeEntryCodes: [],
    requiresDescription: false,
    requiresApproval: false,
    allowWeekendEntry: true,
    budget: undefined, // Not available in list item
    objectives: [],
    deliverables: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
